import { isValidTckn } from 'src/app/core/helpers/identity-document.helpers';
import { MrzTd1Result, MrzTd3Result } from './identity-ocr.models';

/**
 * ICAO 9303 TD1 (T.C. Kimlik Kartı arka yüzü, 3×30) ayrıştırıcısı — masak-kimlik-ocr.md §2, §4.
 *
 * OCR gerçekleri (Faz 0 spike): "<" dolgusu sık sık "K" ya da "L" okunur ve dolgu bölgelerinde
 * karakter sayısı tutmaz; satır 1 ve satır 2'nin ilk 18 karakteri ise güvenilir. Bu yüzden alanlar
 * sabit konumdan, alan türüne göre karakter düzeltmesiyle alınır; bileşik kontrol hanesi satırın
 * SON karakteridir. Ad satırı yalnızca ön yüzdeki adı doğrulamak için kullanılır.
 */

const TO_DIGIT: Record<string, string> = { O: '0', Q: '0', D: '0', U: '0', I: '1', L: '1', T: '1', Z: '2', S: '5', B: '8', G: '6' };
const TO_ALPHA: Record<string, string> = { '0': 'O', '1': 'I', '2': 'Z', '5': 'S', '8': 'B', '6': 'G' };

const toDigits = (s: string) => s.split('').map((c) => TO_DIGIT[c] ?? c).join('');
const toAlphas = (s: string) => s.split('').map((c) => TO_ALPHA[c] ?? c).join('');

function charValue(c: string): number {
  if (c === '<') return 0;
  if (c >= '0' && c <= '9') return c.charCodeAt(0) - 48;
  return c.charCodeAt(0) - 55;
}

/** 7-3-1 ağırlıklı ICAO kontrol hanesi. */
export function mrzCheckDigit(value: string): number {
  return value.split('').reduce((sum, c, i) => sum + charValue(c) * [7, 3, 1][i % 3], 0) % 10;
}

/** Ğ→G, Ş→S, İ→I … — MRZ'de Türkçe karakter yok. */
export function foldTurkish(value: string): string {
  return value
    .toLocaleUpperCase('tr-TR')
    .replace(/Ğ/g, 'G').replace(/Ü/g, 'U').replace(/Ş/g, 'S')
    .replace(/İ/g, 'I').replace(/Ö/g, 'O').replace(/Ç/g, 'C');
}

/** OCR metninde "I<TUR" ile başlayan satırı ve ardındaki iki satırı bulur. */
export function findTd1Lines(text: string): [string, string, string] | null {
  const lines = text.split('\n').map((l) => l.replace(/\s+/g, '').replace(/[«‹]/g, '<').toUpperCase());
  const start = lines.findIndex((l) => /^I[<A-Z]TUR/.test(l));
  if (start < 0 || start + 2 >= lines.length) return null;
  const [l1, l2, l3] = lines.slice(start, start + 3);
  if (l1.length < 26 || l2.length < 26 || l3.length < 5) return null;
  return [l1, l2, l3];
}

/** YYAAGG → yyyy-MM-dd. Doğum tarihi gelecekte kalamaz; son geçerlilik her zaman 2000 sonrası. */
export function mrzDate(value: string, kind: 'birth' | 'expiry', today: Date = new Date()): string | undefined {
  if (!/^\d{6}$/.test(value)) return undefined;
  const yy = +value.substring(0, 2);
  const mm = +value.substring(2, 4);
  const dd = +value.substring(4, 6);
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return undefined;
  const currentYy = today.getFullYear() % 100;
  const year = kind === 'expiry' ? 2000 + yy : yy > currentYy ? 1900 + yy : 2000 + yy;
  return `${year}-${value.substring(2, 4)}-${value.substring(4, 6)}`;
}

export function parseTd1(lines: [string, string, string], today: Date = new Date()): MrzTd1Result {
  const [l1, l2, l3] = lines;
  // Seri no biçimi A12B34567: 1. ve 4. karakter harf, diğerleri rakam.
  const serial = toAlphas(l1[5]) + toDigits(l1.substring(6, 8)) + toAlphas(l1[8]) + toDigits(l1.substring(9, 14));
  const serialCheck = toDigits(l1[14] ?? '');
  // TCKN opsiyonel alanda 16. karakterden başlıyor (15. karakter "<"); konum varsayılmadan aranır.
  const tckn = toDigits(l1.substring(15)).match(/\d{11}/)?.[0];
  const birth = toDigits(l2.substring(0, 6));
  const birthCheck = toDigits(l2[6] ?? '');
  const genderChar = l2[7];
  const expiry = toDigits(l2.substring(8, 14));
  const expiryCheck = toDigits(l2[14] ?? '');
  const nationality = toAlphas(l2.substring(15, 18));
  // Bileşik hane satırın son karakteri; satır sonu en kötü okunan yer (tarayıcıda "6" → "H"). Dolgu
  // K/L okunabildiği için harf düzeltmesi YAPILMAZ: yalnızca gerçek rakam olan son karakter alınır.
  const composite = l2.substring(18).match(/(\d)[^\d]*$/)?.[1] ?? '';

  // TR kartında satır 1 opsiyonel alanı "<" + TCKN + "<<<", satır 2 opsiyonel alanı boş.
  const optional1 = ('<' + (tckn ?? '') + '<'.repeat(15)).substring(0, 15);
  const compositeSource = serial + serialCheck + optional1 + birth + birthCheck + expiry + expiryCheck + '<'.repeat(11);

  const digitOk = (value: string, check: string) => /^\d$/.test(check) && mrzCheckDigit(value) === +check;
  return {
    documentCode: l1.substring(0, 2),
    country: toAlphas(l1.substring(2, 5)),
    serialNumber: serial,
    serialNumberOk: digitOk(serial, serialCheck),
    tckn,
    tcknOk: !!tckn && isValidTckn(tckn),
    birthDate: mrzDate(birth, 'birth', today),
    birthDateOk: digitOk(birth, birthCheck),
    gender: genderChar === 'M' || genderChar === 'F' ? genderChar : undefined,
    expiryDate: mrzDate(expiry, 'expiry', today),
    expiryDateOk: digitOk(expiry, expiryCheck),
    nationalityCode: nationality,
    compositeOk: digitOk(compositeSource, composite),
    nameLine: l3,
  };
}

/**
 * Pasaport (ICAO 9303 TD3, 2×44) MRZ satırlarını bulur: "P" + belge türü/"<" + ülke kodu ile başlayan
 * satır ve ardındaki satır.
 */
export function findTd3Lines(text: string): [string, string] | null {
  const lines = text.split('\n').map((l) => l.replace(/\s+/g, '').replace(/[«‹]/g, '<').toUpperCase());
  // 2. satır yapısından tanınır (uyruk + doğum + cinsiyet + son geçerlilik bloğu); 1. satır "P" ile
  // başlayan bir önceki (ya da iki önceki) satır. Sayfadaki "P" ile başlayan gürültü satırları elenir.
  const l2Index = lines.findIndex((l) => l.length >= 30 && td3AnchorIndex(l) >= 0);
  if (l2Index < 1) return null;
  const l1 = [lines[l2Index - 1], lines[l2Index - 2]].find((l) => !!l && l.startsWith('P') && l.length >= 20);
  return l1 ? [l1, lines[l2Index]] : null;
}

/** Rakam alanında OCR'ın harf okuyabildiği karakterler (bkz. TO_DIGIT). */
const DIGITISH = '[0-9OQDUILTZSBG]';
const TD3_ANCHOR = new RegExp(`([A-Z<]{3})(${DIGITISH}{6})(${DIGITISH})([MF<])(${DIGITISH}{6})(${DIGITISH})`);

/**
 * 2. satırda uyruk bloğunun başladığı konum (kusursuz satırda 10). OCR baştan karakter düşürür ya da
 * ekler — satır bu bloğa göre yeniden hizalanır. Pasaport no + kontrol hanesinin içinde eşleşme
 * olmasın diye en az 6. konumdan aranır.
 */
function td3AnchorIndex(l2: string): number {
  const match = TD3_ANCHOR.exec(l2.substring(6));
  return match ? match.index + 6 : -1;
}

/**
 * TD3 ayrıştırıcısı. Satır 2: pasaport no (9) + kontrol, uyruk (3), doğum (6) + kontrol, cinsiyet,
 * son geçerlilik (6) + kontrol, kişisel numara (14) + kontrol, bileşik kontrol. İlk 28 karakterde
 * dolgu yok, sabit konumdan güvenle okunur; kişisel numaranın dolgusu ("<<<") OCR'da K/L okunabildiği
 * için TCKN alan içinde aranır, bileşik hane satırın son gerçek rakamıdır (bkz. parseTd1).
 */
export function parseTd3(lines: [string, string], today: Date = new Date()): MrzTd3Result {
  const [l1, rawL2] = lines;
  const digitOk = (value: string, check: string) => /^\d$/.test(check) && mrzCheckDigit(value) === +check;

  // Uyruk bloğu 10. konuma gelecek biçimde hizala; baştan düşen karakter "<" ile doldurulur (o
  // zaman pasaport no kontrol hanesi tutmaz, tarihler yine okunur).
  const anchor = td3AnchorIndex(rawL2);
  const l2 = anchor < 0 || anchor === 10 ? rawL2 : anchor > 10 ? rawL2.substring(anchor - 10) : '<'.repeat(10 - anchor) + rawL2;

  const numberCheck = toDigits(l2[9] ?? '');
  const rawNumber = l2.substring(0, 9);
  // Pasaport no harf-rakam karışık: önce olduğu gibi, tutmazsa Türk pasaportu biçimiyle (1 harf + 8 rakam).
  const passportNumber = [rawNumber, toAlphas(rawNumber[0] ?? '') + toDigits(rawNumber.substring(1))]
    .find((candidate) => digitOk(candidate, numberCheck)) ?? rawNumber;

  const birth = toDigits(l2.substring(13, 19));
  const birthCheck = toDigits(l2[19] ?? '');
  const genderChar = l2[20];
  const expiry = toDigits(l2.substring(21, 27));
  const expiryCheck = toDigits(l2[27] ?? '');

  const tcknCandidate = toDigits(l2.substring(28, 40)).match(/\d{11}/)?.[0];
  const tckn = tcknCandidate && isValidTckn(tcknCandidate) ? tcknCandidate : undefined;
  const personal = (tckn ?? '').padEnd(14, '<');
  const composite = l2.substring(28).match(/(\d)[^\d]*$/)?.[1] ?? '';
  const compositeSource = passportNumber + numberCheck + birth + birthCheck + expiry + expiryCheck
    + personal + mrzCheckDigit(personal);

  // 1. satır: "P<" + düzenleyen ülke + ad. OCR "P<TUR"u "PLXTUR"/"PATUR" okuyabiliyor; ad kısmı
  // ülke kodunun (genelde uyrukla aynı) bulunduğu yerden sonra başlar.
  const nationalityCode = toAlphas(l2.substring(10, 13));
  const countryAt = l1.substring(1, 8).indexOf(nationalityCode);
  const nameStart = countryAt >= 0 ? countryAt + 1 + 3 : 5;

  return {
    documentCode: l1.substring(0, 2),
    issuingCountry: toAlphas(l1.substring(nameStart - 3, nameStart)),
    passportNumber: passportNumber.replace(/</g, ''),
    passportNumberOk: digitOk(passportNumber, numberCheck),
    nationalityCode,
    birthDate: mrzDate(birth, 'birth', today),
    birthDateOk: digitOk(birth, birthCheck),
    gender: genderChar === 'M' || genderChar === 'F' ? genderChar : undefined,
    expiryDate: mrzDate(expiry, 'expiry', today),
    expiryDateOk: digitOk(expiry, expiryCheck),
    tckn,
    tcknOk: !!tckn,
    compositeOk: digitOk(compositeSource, composite),
    nameLine: l1.substring(nameStart),
  };
}

/**
 * Ön yüzdeki Türkçe karakterli büyük harfli sözcüklerden MRZ ad satırına oturan diziyi seçer.
 * Dolgu "<" OCR'da K/L okunabildiği için sözcük aralarındaki [<KL] dizisinin ne kadarının dolgu,
 * ne kadarının sonraki adın baş harfi olduğu belirsizdir ("<<KEMAL"): her ayırıcı uzunluğu geri
 * izlemeyle denenir, satıra baştan sona oturan ilk dizi alınır. İlk 2+ karakterlik ayırıcı ("<<")
 * soyadını adlardan ayırır.
 *
 * Ön yüzde okunamamış bir sözcük, MRZ'de gerçek "<" ile sınırlanmışsa MRZ'den alınır (Türkçe
 * karaktersiz) — o zaman fromMrzOnly = true: ad "kontrol edin" sayılır. Oturan dizi yoksa null.
 *
 * Arama konuma göre belleklenir (sonuç yalnızca konuma bağlı): gürültülü okumada aday sözcük ve
 * dolgu birleşimleri üstel büyüyüp sayfayı kilitliyordu. Yalnızca K/L'den oluşan sözcükler (zemin
 * gürültüsü; dolgudan ayırt edilemez) aday sayılmaz.
 */
export function matchMrzNames(
  words: string[],
  nameLine: string,
): { surname: string; given: string; fromMrzOnly: boolean } | null {
  // TD1 ad satırı 30 karakter; OCR'ın eklediği taşkın çöp aramayı büyütmesin.
  const line = nameLine.substring(0, 34);
  const folded = Array.from(new Set(words))
    .map((w) => ({ word: w, folded: foldTurkish(w) }))
    .filter((w) => w.folded.length >= 2 && !/^[KL]+$/.test(w.folded))
    .sort((a, b) => b.folded.length - a.folded.length);
  type Token = { word: string; separator: number; mrzOnly: boolean };

  const memo = new Map<number, Token[] | null>();
  const solve = (p: number): Token[] | null => {
    if (memo.has(p)) return memo.get(p)!;
    const first = p === 0;
    const rest = line.substring(p);
    let result: Token[] | null = null;
    if (/^[<KL]*$/.test(rest)) {
      result = first ? null : [];
    } else {
      const run = rest.match(/^[<KL]*/)![0].length;
      search: for (let s = first ? 0 : 1; s <= run; s++) {
        const at = rest.substring(s);
        for (const w of folded) {
          if (!at.startsWith(w.folded) || !/^([<KL]|$)/.test(at.substring(w.folded.length))) continue;
          const tail = solve(p + s + w.folded.length);
          if (tail) {
            result = [{ word: w.word, separator: s, mrzOnly: false }, ...tail];
            break search;
          }
        }
      }
      // Ön yüzde yok: yalnızca gerçek "<" ile biten (K/L belirsizliği olmayan) MRZ sözcüğü.
      for (let s = first ? 0 : 1; !result && s <= run; s++) {
        const mrzWord = rest.substring(s).match(/^[A-Z]{2,}(?=<)/)?.[0];
        if (!mrzWord) continue;
        const tail = solve(p + s + mrzWord.length);
        if (tail) result = [{ word: mrzWord, separator: s, mrzOnly: true }, ...tail];
      }
    }
    memo.set(p, result);
    return result;
  };

  const tokens = solve(0);
  if (!tokens || tokens.every((t) => t.mrzOnly)) return null;
  const split = tokens.findIndex((t, i) => i > 0 && t.separator >= 2);
  if (split < 0) return null;
  return {
    surname: tokens.slice(0, split).map((t) => t.word).join(' '),
    given: tokens.slice(split).map((t) => t.word).join(' '),
    fromMrzOnly: tokens.some((t) => t.mrzOnly),
  };
}
